// Option editor slice. Mirrors product-editor-slice: holds the *draft* of the
// option currently being edited inside the catalog board's option dialog,
// kept separate from `adminCatalog` so there's a single source of truth for
// in-progress edits (no copying server rows into component-local state).
// DEFAULT_DRAFT lives in initialState, so opening a "new option" seeds from
// defaults and closing the dialog reverts the draft back to those defaults.
import { createSlice } from '@reduxjs/toolkit';
import { saveCatalogItem, savePricing, clearSaveError } from './admin-catalog-slice.js';
import { findOptionTiers, findOptionRule } from '../utils/catalog-helpers.js';
import { tiersFromRule, emptyTiers } from '../config/pricing-buckets.js';
import {
  DEFAULT_STRATEGY, strategyFromRule, amountFromRule,
} from '../config/pricing-strategies.js';

const DEFAULT_DRAFT = {
  name: '',
  short_description: '',
  display_order: 0,
  is_active: true,
};

const initialState = {
  open: false,
  optionId: null,
  productId: null,
  isNew: false,
  draft: DEFAULT_DRAFT,
  tiers: emptyTiers(),
  strategy: DEFAULT_STRATEGY,
  flatAmount: 0,
};

const optionEditorSlice = createSlice({
  name: 'optionEditor',
  initialState,
  reducers: {
    openEditor: (state, action) => {
      const {
        optionId, productId, isNew, draft, tiers, strategy, flatAmount,
      } = action.payload;
      state.open = true;
      state.optionId = optionId;
      state.productId = productId;
      state.isNew = isNew;
      state.draft = draft;
      state.tiers = tiers;
      state.strategy = strategy;
      state.flatAmount = flatAmount;
    },
    closeEditor: () => initialState,
    setDraftField: (state, action) => {
      const { field, value } = action.payload;
      state.draft[field] = value;
    },
    setDraftName: (state, action) => {
      state.draft.name = action.payload;
    },
    setTiers: (state, action) => { state.tiers = action.payload; },
    setStrategy: (state, action) => { state.strategy = action.payload; },
    setFlatAmount: (state, action) => { state.flatAmount = action.payload; },
    setOptionId: (state, action) => { state.optionId = action.payload; },
  },
});

export const {
  openEditor, closeEditor, setDraftField, setDraftName,
  setTiers, setStrategy, setFlatAmount, setOptionId,
} = optionEditorSlice.actions;

// Open the editor for an existing option, or a new one under `productId`.
// Resolves the draft and pricing (tiers/strategy/amount) from the catalog slice
// so the dialog itself never derives from server data.
export const openOptionEditor = ({ option, productId } = {}) => (dispatch, getState) => {
  const { pricing } = getState().adminCatalog.data;
  const rule = option ? findOptionRule(pricing, option.id) : null;
  dispatch(clearSaveError());
  dispatch(openEditor({
    optionId: option?.id ?? null,
    productId,
    isNew: !option,
    draft: { ...DEFAULT_DRAFT, ...(option || {}) },
    tiers: tiersFromRule(option ? findOptionTiers(pricing, option.id) : null),
    strategy: strategyFromRule(rule),
    flatAmount: amountFromRule(rule),
  }));
};

// Save the draft: upsert the option row, then upsert its pricing rule.
// Two writes because the pricing target is the option id, which doesn't exist
// until the option row is created. Returns `{ ok }` so the dialog can close.
export const saveOptionDraft = () => async (dispatch, getState) => {
  const {
    draft, tiers, strategy, flatAmount, optionId, productId,
  } = getState().optionEditor;

  const saved = await dispatch(saveCatalogItem({
    resource: 'options',
    id: optionId,
    values: {
      name: draft.name.trim(),
      product_id: productId,
      short_description: draft.short_description?.trim() || null,
      display_order: Number(draft.display_order) || 0,
      is_active: draft.is_active,
    },
  }));
  if (!saveCatalogItem.fulfilled.match(saved)) return { ok: false };

  const { item } = saved.payload;
  dispatch(setOptionId(item.id));

  const priced = await dispatch(savePricing({
    target: { product_option_id: item.id },
    strategy,
    tiers,
    amount: Number(flatAmount) || 0,
  }));
  return { ok: savePricing.fulfilled.match(priced) };
};

export default optionEditorSlice.reducer;
